import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PaintbrushIcon, PaletteIcon, PencilRulerIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/c5nA3gZVYhqrjucaazWph9/Limpieza_precisa_del_empaque_de_seis_libros.webp';
export const PRODUCT_IMAGE_ALT = 'BILDKLAR-paketet: sex bildvolymer för årskurs 1–9 med lektionsupplägg, elevblad, kreativa projekt och bedömningsstöd.';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '300',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala bildvolymer'
}, {
  value: '9',
  label: 'Årskurser – från 1 till 9'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '1',
  label: 'Samlat undervisningspaket'
}];
export const differenceStats = [{
  value: 'Årskurs 1–9',
  label: 'Material organiserat i tre stadier'
}, {
  value: '6 digitala volymer',
  label: 'Från färg och form till visuella budskap och bildanalys'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut i klassrummet'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas bildlektioner – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande',
  text: 'Idéer från olika webbplatser är för enkla, för avancerade eller kräver material som du inte har.'
}, {
  title: 'Elevbladen skapas från grunden',
  text: 'Instruktioner, bildexempel, arbetssteg och reflektionsfrågor – det som skulle ta minuter blir en lång arbetsuppgift.'
}, {
  title: 'Svårt att göra instruktionerna visuella',
  text: 'Färgblandningar, perspektiv, komposition och olika tekniker tar tid att illustrera inför varje nytt moment.'
}, {
  title: 'Bedömning och uppföljning tar tid',
  text: 'Att dokumentera arbetsprocessen, formulera återkoppling och följa elevernas utveckling blir ännu mer arbete efter skoltid.'
}, {
  title: 'Stress inför nästa lektion',
  text: 'När tiden inte räcker till blir det lätt att improvisera och få ihop en bilduppgift i sista minuten.'
}];
export const afterItems = ['Ett organiserat bildpaket för hela grundskolan', 'Lektionsupplägg som är enkla att använda', 'Färdiga kreativa uppgifter', 'Elevblad som kan skrivas ut direkt', 'Visuella förklaringar och illustrerade arbetssteg', 'Övningar för att utforska material och tekniker', 'Bildprojekt som skapar variation', 'Bedömningsstöd och reflektionsfrågor', 'Planeringsstöd för att organisera läsårets undervisning'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Söndagarna går åt till planering',
  text: 'Lägger du delar av helgen på att hitta bildidéer och förbereda nästa veckas undervisning?'
}, {
  icon: FolderSearchIcon,
  title: 'Du vet inte vilken uppgift du ska använda',
  text: 'Letar du ständigt efter aktiviteter som passar elevernas ålder, förkunskaper och tillgängliga material?'
}, {
  icon: LightbulbIcon,
  title: 'Du vill ha tydligare förklaringar',
  text: 'Behöver du mer visuellt stöd för att förklara färg, form, tekniker och bildskapande på ett enkelt sätt?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektionsupplägg, instruktioner och elevuppgifter redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna behöver mer variation',
  text: 'Vill du använda fler tekniker, kreativa utmaningar och praktiska projekt i undervisningen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha materialet samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Åk 1–3',
  title: 'Färg & form',
  tagline: 'Ge eleverna en trygg och kreativ start i bildämnet.',
  intro: 'Den första volymen fokuserar på linjer, former, färger och grundläggande bildskapande.',
  items: ['Linjer och visuella uttryck', 'Geometriska och organiska former', 'Färgblandning och färghjul', 'Varma och kalla färger', 'Mönster och texturer', 'Förgrund, bakgrund och komposition', 'Utskrivbara elevblad', 'Kreativa bildprojekt', 'Reflektion och formativ bedömning'],
  outro: 'Färdigt material som gör bildskapandet konkret, varierat och lätt att komma igång med.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Åk 1–3',
  title: 'Skapa & berätta',
  tagline: 'Låt eleverna berätta med bilder.',
  intro: 'Den andra volymen innehåller kreativa aktiviteter där eleverna skapar bilder som berättar, med enkla tekniker och material.',
  items: ['Bildberättelser och serierutor', 'Figurer, miljöer och händelser', 'Teckning och måleri', 'Collage och klippteknik', 'Enkel konstruktion och form', 'Bildsamtal i klassrummet', 'Utskrivbara elevblad', 'Kreativa bildprojekt', 'Reflektion och formativ bedömning'],
  outro: 'Färdiga uppgifter där eleverna får fantisera, skapa och berätta om sina bilder.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Åk 4–6',
  title: 'Teknik & perspektiv',
  tagline: 'Utveckla elevernas tekniker och bildförståelse.',
  intro: 'Den tredje volymen hjälper eleverna att pröva olika tekniker och skapa djup och rumslighet i sina bilder.',
  items: ['Skissteknik och observation', 'Ljus, skugga och volym', 'Perspektiv och djup', 'Färglära och nyanser', 'Måleri- och tryckteknik', 'Illustrerade arbetssteg', 'Utskrivbara elevblad', 'Kreativa bildprojekt', 'Bedömningsstöd och reflektion'],
  outro: 'Tydliga exempel och övningar för bildundervisningen i årskurs 4–6.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Åk 4–6',
  title: 'Bilder & budskap',
  tagline: 'Hjälp eleverna att förstå hur bilder kommunicerar.',
  intro: 'Den fjärde volymen handlar om hur bilder förmedlar budskap – och hur eleverna kan skapa egna.',
  items: ['Bild och text tillsammans', 'Affischer och informationsbilder', 'Symboler och visuella tecken', 'Komposition och blickfång', 'Reklambilder och påverkan', 'Bildsamtal och tolkning', 'Utskrivbara elevblad', 'Kreativa bildprojekt', 'Bedömningsstöd och reflektion'],
  outro: 'Ett praktiskt stöd för att arbeta med visuella budskap i klassrummet.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Åk 7–9',
  title: 'Uttryck & identitet',
  tagline: 'Ge eleverna verktyg att uttrycka sig själva.',
  intro: 'Den femte volymen innehåller uppgifter där eleverna utforskar personliga uttryck, porträtt och identitet.',
  items: ['Porträtt och självporträtt', 'Personligt bildspråk', 'Stil och uttrycksmedel', 'Blandteknik', 'Skissbok och idéutveckling', 'Konstnärliga förebilder', 'Utskrivbara elevblad', 'Kreativa bildprojekt', 'Bedömningsstöd och reflektion'],
  outro: 'Strukturerade resurser för ett mer personligt bildskapande på högstadiet.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Åk 7–9',
  title: 'Bildanalys & projekt',
  tagline: 'Låt eleverna analysera, planera och genomföra egna bildprojekt.',
  intro: 'Den sjätte volymen kombinerar bildanalys med längre projekt där eleverna arbetar från idé till färdigt verk.',
  items: ['Modeller för bildanalys', 'Konst- och bildhistoriska exempel', 'Analys av medie- och reklambilder', 'Projektplanering', 'Idé, skiss och genomförande', 'Presentation av eget arbete', 'Utskrivbara elevblad', 'Kommenterade exempel', 'Bedömningsstöd och reflektion'],
  outro: 'Ett samlat stöd för analys, fördjupning och elevernas egna projekt.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för årskurs 1–9',
  text: 'Ett samlat bildpaket som hjälper dig att planera och genomföra undervisningen under läsåret. Varje volym innehåller 50 sidor med lektionsmaterial, visuella övningar och kreativa klassrumsaktiviteter.'
};
export const packIntro = 'Bildmaterial för läsårets undervisning – organiserat och redo att använda.';
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga lektioner',
  text: 'Slipp börja från ett tomt dokument. Tydliga lektionsupplägg, exempel och aktiviteter hjälper dig att komma igång snabbare.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara elevblad',
  text: 'Bildövningar som kan skrivas ut och användas direkt – för att utforska, skissa, planera och reflektera.'
}, {
  icon: PaletteIcon,
  title: 'Visuella bildövningar',
  text: 'Illustrerade arbetssteg, färgexempel och tydliga bildjämförelser gör tekniker och begrepp mer konkreta och lättare att förstå.'
}, {
  icon: PaintbrushIcon,
  title: 'Kreativa projekt och aktiviteter',
  text: 'Teckning, måleri, collage, konstruktion och visuellt berättande ger eleverna fler sätt att utveckla sina idéer.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömningsstöd och återkoppling',
  text: 'Observationsunderlag, reflektionsfrågor och kommenterade exempel hjälper dig att följa elevernas arbetsprocess och utveckling.'
}, {
  icon: CalendarCheckIcon,
  title: 'Planeringsstöd',
  text: 'Stöd för att organisera bildundervisningen och välja arbetsområden utifrån din grupp, din tid och dina material.'
}];
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/283f4c70-6702-4e68-8924-03b8048b6513.jpg',
  label: 'Elevblad 08',
  title: 'Fortsätt linjen',
  text: 'Utforska raka, böjda, vågiga och sicksackformade linjer – med tydliga bildstarter och utrymme för egna variationer.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/decbcfc2-8027-438f-9621-7e959bbe8c63.jpg',
  label: 'Elevblad 17',
  title: 'Från form till figur',
  text: 'Förvandla enkla former till djur, växter, föremål och fantasifigurer med hjälp av kreativa bildstarter.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/39326258-d262-4aec-b1b8-4b1973416e44.jpg',
  label: 'Elevblad 20',
  title: 'Blanda och upptäck',
  text: 'Eleverna blandar färger, dokumenterar sina färgprover och berättar vad de upptäcker.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/396149c2-10d0-42dd-ad7c-9e448f8b840a.jpg',
  label: 'Elevblad 34',
  title: 'Nära och långt bort',
  text: 'Förgrund, bakgrund och överlappning – eleverna skapar en egen bild och undersöker hur djup kan uppstå.'
}, {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/9d0a0bf3-fe59-4c5e-ba84-a1e601694cae.jpg',
  label: 'Kunskapskoll – Linjer och former',
  title: 'Årskurs 1–3',
  text: 'Ett strukturerat bedömningsblad med visuella uppgifter och plats för elevsvar, tillsammans med stöd för lärarens uppföljning.'
}];
export const pageTypes = ['Färdiga lektioner', 'Utskrivbara elevblad', 'Kreativa projekt', 'Bedömning och reflektion'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikariepaketet',
  text: 'Tolv reservlektioner med enkla material – fyra för varje stadium – när du snabbt behöver ett tydligt upplägg.',
  items: ['12 färdiga lektionsbeskrivningar', 'Mål, material och tidsplan', 'Konkreta elevinstruktioner', 'Anpassningar och reflektionsfrågor'],
  benefit: 'Mindre tid på förberedelser när du behöver en lektion med kort varsel.'
}, {
  title: 'Snabbstart',
  text: 'Trettio kreativa uppvärmningar på 5–10 minuter som hjälper eleverna att komma igång.',
  items: ['30 konkreta bildövningar', 'Aktiviteter för tre stadier', 'Tydliga instruktioner och exempel', 'Stödvarianter och extra utmaningar'],
  benefit: 'Perfekt när du vill väcka idéer och få igång skapandet.'
}, {
  title: 'Bildstöd i bildsalen',
  text: 'En praktisk guide till tydligare instruktioner, arbetssekvenser och rutiner i bildundervisningen.',
  items: ['Materialordlista med förklaringar', 'Stegvisa arbetsinstruktioner', 'Rutiner för start och avslut', 'Förslag på stöd och anpassningar'],
  benefit: 'Gör det lättare för eleverna att förstå vad de ska göra och arbeta mer självständigt.'
}, {
  title: 'Materialsmart – skapa med liten budget',
  text: 'Praktiska sätt att använda enkla material, planera inköp och ta vara på det som redan finns.',
  items: ['Konkreta materialalternativ', 'Idéer med återbruksmaterial', 'Inventering och förberedelselistor', 'Organisation, förvaring och städning'],
  benefit: 'Ett praktiskt stöd för att skapa mer med tillgängliga resurser.'
}, {
  title: 'Klassens konstutställning',
  text: 'En guide som hjälper dig och eleverna att planera, presentera och följa upp en egen konstutställning.',
  items: ['Planering och elevroller', 'Textförslag till inbjudningar och verksetiketter', 'Stöd för elevernas konstnärspresentationer', 'Besökarfrågor och återkoppling'],
  benefit: 'Ge elevernas arbete en tydlig avslutning och en publik.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna BILDKLAR™',
  text: 'Ladda ner paketet och öppna volymen som passar din årskurs.'
}, {
  title: 'Välj lektion och material',
  text: 'Hitta rätt arbetsområde och välj de uppgifter, elevblad eller projekt som passar dina elever.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom instruktionerna och välj det material du behöver. Utskrift, framplockning och mer omfattande projekt kan kräva extra tid.'
}, {
  title: 'Skriv ut och undervisa',
  text: 'Skriv ut elevbladen eller visa instruktionerna digitalt – och gå in i klassrummet med en tydlig plan.'
}];
export const audienceItems = ['Undervisar i bild i årskurs 1–3, 4–6 eller 7–9', 'Vill ha bildmaterial för läsåret samlat och organiserat', 'Vill slippa skapa varje elevuppgift från grunden', 'Behöver fler visuella förklaringar och bildexempel', 'Vill använda varierade tekniker och kreativa projekt', 'Vill ha enkel tillgång till bedömningsstöd och reflektionsfrågor', 'Vill spara tid på veckoplaneringen', 'Vill känna sig bättre förberedd inför undervisningen'];
export const timeValueTasks = ['Leta efter användbara bildidéer', 'Skriva elevinstruktioner', 'Skapa visuella exempel', 'Förbereda projekt och material', 'Dokumentera och följa upp elevernas arbete'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 300 sidor',
  detail: 'Färg, form, visuellt berättande, tekniker, perspektiv, identitet och bildanalys'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Reservlektioner, kreativa uppvärmningar, bildstöd, materialguide och klassens konstutställning'
}, {
  label: 'För årskurs 1–9',
  detail: 'Färdiga lektionsupplägg, elevblad, kreativa projekt och bedömningsstöd'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara elevblad', 'Ingen fysisk produkt skickas'];
export const PRICE = '299\u00A0kr';

// Only add authentic reviews of BILDKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'BILDKLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de elevblad du behöver.'
}, {
  q: 'Är BILDKLAR™ anpassat för den svenska skolan?',
  a: 'Materialet är skrivet på svenska och organiserat för årskurs 1–3, 4–6 och 7–9. Innehållet behandlar bland annat bildskapande, tekniker, visuella budskap och bildanalys. Du väljer och anpassar uppgifterna efter din undervisning. BILDKLAR™ är ett fristående material, inte en officiell produkt från Skolverket.'
}, {
  q: 'Passar det för årskurs 1–9?',
  a: 'Ja. Paketet innehåller två volymer för varje stadium: årskurs 1–3, 4–6 och 7–9. Välj uppgifter och stöd utifrån elevernas förkunskaper och behov.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'För ett enkelt upplägg kan fem minuter räcka för att läsa instruktionerna och välja aktivitet. Utskrift, materialförberedelser, anpassningar och större projekt kan ta längre tid.'
}, {
  q: 'Räcker materialet för ett helt läsår?',
  a: 'Paketet är utformat som stöd för läsårets planering. Hur långt det räcker beror på antal lektioner, projektens längd och vilka uppgifter du väljer. Det innehåller material för tre stadier, inte nio separata fullständiga årsprogram.'
}, {
  q: 'Finns det färdiga elevblad?',
  a: 'Ja. Huvudvolymerna innehåller elevblad för bland annat bildövningar, skisser, planering och reflektion. Vissa praktiska uppgifter genomförs på separat papper eller med andra material.'
}, {
  q: 'Ingår facit?',
  a: 'Till uppgifter med bestämda svar finns svarsstöd. Öppna skapandeuppgifter kompletteras med exempel, reflektionsfrågor och bedömningsstöd – eftersom en kreativ uppgift kan ha många olika lösningar.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. Materialet innehåller stöd och förslag på variationer. Du kan förenkla instruktioner, minska antalet moment eller lägga till fördjupning utifrån elevgruppen.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Skriv ut de elevblad du behöver och visa övriga instruktioner och bildexempel digitalt. Många projekt kan göras på vanligt ritpapper.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: 'Ja. Vikariepaketet, Snabbstart, Bildstöd i bildsalen, Materialsmart och Klassens konstutställning ingår utan extra kostnad. Bonusarna är kompletterande lärarguider med praktiska instruktioner och exempel.'
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång för paketet. Ingen återkommande prenumerationsavgift tillkommer.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner PDF-filerna och spara dem för användning i din egen undervisning.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på mobil, surfplatta och dator med en PDF-läsare. För utskrift och överblick är en större skärm ofta enklare.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. Paketet omfattas av 7 dagars nöjdhetsgaranti enligt våra garantivillkor. Kontakta oss via e-post inom 7 dagar från köpet om materialet inte motsvarar dina förväntningar.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
